import React, { useEffect, useRef } from 'react';

type Particle = { x: number; y: number; bx: number; by: number; s: number };

export const PortalPixelBubble: React.FC<{
  size?: number;
  active?: boolean;
  interactive?: boolean;
  color?: string;
  className?: string;
}> = ({ size = 56, active = false, interactive = true, color = '#22d3ee', className = '' }) => {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const mouseRef = useRef({ x: -999, y: -999 });
  const particlesRef = useRef<Particle[]>([]);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const dpr = Math.min(window.devicePixelRatio || 1, 2);
    canvas.width = size * dpr;
    canvas.height = size * dpr;
    canvas.style.width = `${size}px`;
    canvas.style.height = `${size}px`;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);

    const count = Math.floor((size * size) / 90);
    particlesRef.current = Array.from({ length: count }, () => ({
      x: Math.random() * size,
      y: Math.random() * size,
      bx: Math.random() * size,
      by: Math.random() * size,
      s: Math.random() > 0.65 ? 2 : 1,
    }));

    let raf = 0;
    let t = 0;
    const draw = () => {
      t += 0.016;
      const pulse = active ? 0.55 + Math.sin(t * 4.2) * 0.25 : 0.35;
      ctx.clearRect(0, 0, size, size);
      ctx.fillStyle = 'rgba(2, 6, 23, 0.92)';
      ctx.beginPath();
      ctx.arc(size / 2, size / 2, size / 2 - 1, 0, Math.PI * 2);
      ctx.fill();

      for (const p of particlesRef.current) {
        if (interactive && mouseRef.current.x > -100) {
          const dx = p.x - mouseRef.current.x;
          const dy = p.y - mouseRef.current.y;
          const dist = Math.hypot(dx, dy);
          if (dist < 28 && dist > 0.1) {
            const force = (28 - dist) * 0.04;
            p.x += (dx / dist) * force;
            p.y += (dy / dist) * force;
          }
        }
        p.x += (p.bx - p.x) * 0.04;
        p.y += (p.by - p.y) * 0.04;
        ctx.fillStyle = color;
        ctx.globalAlpha = pulse;
        ctx.fillRect(p.x, p.y, p.s, p.s);
      }
      ctx.globalAlpha = 1;
      if (active) {
        ctx.strokeStyle = color;
        ctx.globalAlpha = 0.25 + Math.sin(t * 4.2) * 0.15;
        ctx.lineWidth = 1.5;
        ctx.beginPath();
        ctx.arc(size / 2, size / 2, size / 2 - 2, 0, Math.PI * 2);
        ctx.stroke();
        ctx.globalAlpha = 1;
      }
      raf = requestAnimationFrame(draw);
    };
    draw();

    const onMove = (e: MouseEvent) => {
      const r = canvas.getBoundingClientRect();
      mouseRef.current = { x: e.clientX - r.left, y: e.clientY - r.top };
    };
    const onLeave = () => {
      mouseRef.current = { x: -999, y: -999 };
    };
    if (interactive) {
      canvas.addEventListener('mousemove', onMove);
      canvas.addEventListener('mouseleave', onLeave);
    }
    return () => {
      cancelAnimationFrame(raf);
      canvas.removeEventListener('mousemove', onMove);
      canvas.removeEventListener('mouseleave', onLeave);
    };
  }, [size, active, interactive, color]);

  return (
    <canvas
      ref={canvasRef}
      className={`rounded-full shrink-0 ${active ? 'portal-pixel-bubble-active' : ''} ${className}`}
      aria-hidden
    />
  );
};
